<script setup>
const fullWidthMode = inject("fullWidthMode", false);
const halfCol = computed(() => (fullWidthMode ? undefined : 6));
import imgStar from '@/assets/images_manse/yearly-fortune/img_year_0803.png';
import imgBubble from '@/assets/images_manse/yearly-fortune/img_year_0804.png';
import ContentsTitle from './ContentsTitle.vue';
import { transformYearlyFortuneText } from '@/composables/splitSentence';

const props = defineProps({
  userName: {
    type: String,
    default: '',
  },
  isMobile: {
    type: Boolean,
    default: false,
  },
  data: {
    type: Object,
    default: () => ({}),
  },
});

// 데이터 접근
const 종합전략 = computed(() => props.data?.종합전략 || {});

// 왼쪽 영역 데이터
const 운세흐름 = computed(() => 종합전략.value?.운세흐름 || '');
const 운세대응 = computed(() => 종합전략.value?.운세대응 || '');
const 운세종합 = computed(() => 종합전략.value?.운세종합 || '');

const 운세대응Display = computed(() => transformYearlyFortuneText(운세대응.value));
const 운세종합Display = computed(() => transformYearlyFortuneText(운세종합.value));

// 오른쪽 영역 데이터 (신년다짐)
const 신년다짐 = computed(() => 종합전략.value?.신년다짐 || {});
const 신년다짐개념 = computed(() => 신년다짐.value?.개념 || '');
const 신년다짐특징 = computed(() => {
  const raw = 신년다짐.value?.특징 || '';
  return raw ? raw.split('|') : [];
});
const 신년다짐특징요약 = computed(() => {
  const raw = 신년다짐.value?.특징요약 || '';
  return raw ? raw.split(', ') : [];
});

// 신년다짐 개념 포맷팅 (줄바꿈 + 마지막 문장 밑줄 처리)
const 신년다짐개념Display = computed(() => {
  if (!신년다짐개념.value) return '';

  // '. ' 기준으로 문장 분리
  const sentences = 신년다짐개념.value.split(/(?<=\.)\s/);

  if (sentences.length === 0) return 신년다짐개념.value;

  // 마지막 문장에 밑줄 적용
  const lastIdx = sentences.length - 1;
  sentences[lastIdx] = `<mark>${sentences[lastIdx]}</mark>`;

  return sentences.join('<br>');
});

// 운세흐름에서 정점/약한 월과 분기 파싱
const 운세흐름파싱 = computed(() => {
  const text = 운세흐름.value;
  // "올해 운세는 7월에 정점을 찍고, 5월에 약한 기운을 보입니다. 분기별로는 1분기가 가장 좋고 2분기가 점검해야 할 시기입니다."
  const 정점월Match = text.match(/(\d+)월에 정점/);
  const 약한월Match = text.match(/(\d+)월에 약한/);
  const 좋은분기Match = text.match(/(\d+)분기가 가장 좋/);
  const 점검분기Match = text.match(/(\d+)분기가 점검/);

  return {
    정점월: 정점월Match ? `${정점월Match[1]}월` : '',
    약한월: 약한월Match ? `${약한월Match[1]}월` : '',
    좋은분기: 좋은분기Match ? `${좋은분기Match[1]}분기` : '',
    점검분기: 점검분기Match ? `${점검분기Match[1]}분기` : '',
  };
});

// 운세흐름 텍스트에서 강조 처리
const 운세흐름Display = computed(() => {
  if (!운세흐름.value) return '';
  const { 정점월, 약한월, 좋은분기, 점검분기 } = 운세흐름파싱.value;

  let text = 운세흐름.value;
  if (정점월)
    text = text.replace(`${정점월}에`, `<strong class="highlight-red">${정점월}에</strong>`);
  if (약한월)
    text = text.replace(`${약한월}에`, `<strong class="highlight-red">${약한월}에</strong>`);
  if (좋은분기)
    text = text.replace(
      `${좋은분기}가 가장 좋고`,
      `<strong class="highlight-red">${좋은분기}가 가장 좋고</strong>`,
    );
  if (점검분기)
    text = text.replace(
      `${점검분기}가 점검`,
      `<strong class="highlight-red">${점검분기}가 점검</strong>`,
    );
  text = text.replace(/정점/g, '<strong class="highlight-red">정점</strong>');
  text = text.replace(/약한 기운/g, '<strong class="highlight-red">약한 기운</strong>');

  text = transformYearlyFortuneText(text, { keywordClass: 'yf-keyword--red' });

  return text;
});
</script>

<template>
  <VRow class="full-height over-hidden">
    <!-- 왼쪽 컬럼 -->
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-first">
      <VCard class="personality-card full-height right">
        <!-- 타이틀 -->
        <ContentsTitle
          :number="7"
          text="2026년의 여정을 만들어가는 마음의 기준"
          :isMobile="props.isMobile"
        />

        <div class="strategy-wrapper" :class="{ mobile: props.isMobile }">
          <!-- 운세 흐름 섹션 -->
          <div class="flow-section">
            <div class="section-label">🔮 운세 흐름</div>

            <!-- 흐름 다이어그램 -->
            <div class="flow-diagram">
              <!-- 도약의 시기 -->
              <div class="flow-row">
                <div class="flow-circle flow-circle__fill green">도약의 시기</div>
                <div class="flow-arrow green"></div>
                <div class="flow-circle green">
                  <span>{{ 운세흐름파싱?.정점월 || '월' }}</span>
                  <div class="flow-circle__divider"></div>
                  <span>{{ 운세흐름파싱?.좋은분기 || '분기' }}</span>
                </div>
              </div>

              <!-- 조율의 시기 -->
              <div class="flow-row">
                <div class="flow-circle flow-circle__fill red">조율의 시기</div>
                <div class="flow-arrow red"></div>
                <div class="flow-circle red">
                  <span>{{ 운세흐름파싱?.약한월 || '월' }}</span>
                  <div class="flow-circle__divider"></div>
                  <span>{{ 운세흐름파싱?.점검분기 || '분기' }}</span>
                </div>
              </div>
            </div>

            <!-- 운세 흐름 설명 -->
            <div class="flow-description" v-html="운세흐름Display"></div>
          </div>

          <!-- 사주로 짚어보는 포인트 카드 -->
          <div class="insight-card yellow" v-if="운세대응">
            <img :src="imgStar" alt="star" class="card-icon" />
            <div class="card-body">
              <div class="card-title">사주로 짚어보는 포인트</div>
              <div class="card-text" v-html="운세대응Display"></div>
            </div>
          </div>

          <!-- 올해의 핵심 메시지 카드 -->
          <div class="insight-card blue" v-if="운세종합">
            <img :src="imgBubble" alt="bubble" class="card-icon" />
            <div class="card-body">
              <div class="card-title">올해의 핵심 메시지</div>
              <div class="card-text" v-html="운세종합Display"></div>
            </div>
          </div>
        </div>
      </VCard>
    </VCol>

    <!-- 오른쪽 컬럼 -->
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-second">
      <VCard class="personality-card full-height">
        <div class="resolution-wrapper" :class="{ mobile: props.isMobile }">
          <!-- 마음에 새겨보면 좋아요 섹션 -->
          <div class="resolution-section">
            <div class="section-title">🧡 마음에 새겨보면 좋아요.</div>

            <div class="resolution-card">
              <!-- 키워드 태그 -->
              <div class="keyword-tags" v-if="신년다짐특징요약.length">
                <span v-for="(tag, idx) in 신년다짐특징요약" :key="idx" class="keyword-tag">
                  {{ tag }}
                </span>
              </div>

              <!-- 특징 리스트 -->
              <div class="feature-list" v-if="신년다짐특징.length">
                <div
                  v-for="(item, idx) in 신년다짐특징"
                  :key="idx"
                  class="feature-item"
                  v-html="transformYearlyFortuneText(item)"
                ></div>
              </div>
            </div>
          </div>

          <!-- 하단 문구 -->
          <div class="bottom-quote" v-if="신년다짐개념">
            <div class="img_year_0805"></div>
            <div class="icon-quote"></div>
            <div class="quote-text" v-html="신년다짐개념Display"></div>
          </div>
        </div>
      </VCard>
    </VCol>
  </VRow>
</template>

<style scoped lang="scss">
.strategy-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  padding-bottom: 130px;
  overflow-y: auto;
  background: #fff;
}

// 운세 흐름 섹션
.flow-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.section-label {
  color: #333;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 700;
  line-height: 24px;
}

.flow-diagram {
  display: flex;
  flex-direction: column;
  gap: 30px;
  padding: 20px;
  background: #fff;
}

.flow-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
}

.flow-circle {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 120px;
  height: 120px;
  padding: 20px;
  border-radius: 50%;
  font-family: Pretendard;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  text-align: center;
  animation: circlePop 0.4s ease-out backwards;

  &__fill {
    &.green {
      background: rgba(136, 195, 74, 0.05);
    }

    &.red {
      background: rgba(255, 110, 110, 0.05);
    }
  }

  &__divider {
    width: 72px;
    height: 1px;
    background: currentColor;
    opacity: 0.5;
  }

  &.green {
    border: 1px solid rgba(136, 195, 74, 0.5);
    color: #88c34a;
  }

  &.red {
    border: 1px solid rgba(255, 110, 110, 0.5);
    color: #ff6e6e;
  }
}

// 순차적 등장 (첫 번째 행)
.flow-row:nth-child(1) {
  .flow-circle:nth-child(1) {
    animation-delay: 0.1s;
  }
  .flow-arrow {
    animation-delay: 0.2s;
  }
  .flow-circle:nth-child(3) {
    animation-delay: 0.3s;
  }
}

// 순차적 등장 (두 번째 행)
.flow-row:nth-child(2) {
  .flow-circle:nth-child(1) {
    animation-delay: 0.2s;
  }
  .flow-arrow {
    animation-delay: 0.3s;
  }
  .flow-circle:nth-child(3) {
    animation-delay: 0.4s;
  }
}

@keyframes circlePop {
  0% {
    opacity: 0;
    transform: scale(0);
  }
  70% {
    transform: scale(1.1);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

.flow-arrow {
  width: 69px;
  height: 8px;
  flex-shrink: 0;
  animation: arrowGrow 0.3s ease-out backwards;

  &.green {
    background: url('@manseImages/yearly-fortune/img_year_0801.svg') center/contain no-repeat;
  }

  &.red {
    background: url('@manseImages/yearly-fortune/img_year_0802.svg') center/contain no-repeat;
  }
}

@keyframes arrowGrow {
  0% {
    opacity: 0;
    transform: scaleX(0);
  }
  100% {
    opacity: 1;
    transform: scaleX(1);
  }
}

.flow-description {
  padding: 16px;
  color: #333;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 400;
  line-height: 24px;
  text-align: center;

  :deep(.highlight-red) {
    color: #be3331;
    font-weight: 700;
  }
}

// 인사이트 카드
.insight-card {
  display: flex;
  padding: 16px 16px 16px 8px;
  align-items: center;
  justify-content: center;
  border-radius: 12px;

  &.yellow {
    background: #fffbf0;
  }

  &.blue {
    background: #f0f7ff;
  }

  .card-icon {
    width: 97px;
    height: 110px;
    flex-shrink: 0;
  }

  .card-body {
    padding: 16px;
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 8px;
    background-color: #fff;
    border-radius: 8px;
  }

  .card-title {
    color: #333;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 700;
    line-height: 24px;
  }

  .card-text {
    color: #333;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
  }
}

// ========== 오른쪽 영역 (신년다짐) ==========
.resolution-wrapper {
  width: 100%;
  padding: 24px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 24px;
  overflow-y: auto;
  background: #fff;

  &.mobile {
    padding: 24px 0;
    justify-content: flex-start;
    gap: 40px;
  }
}

.resolution-section {
  display: flex;
  flex-direction: column;
  gap: 16px;

  .resolution-wrapper.mobile & {
    padding: 0 16px;
    gap: 8px;
  }
}

.section-title {
  margin: 0;
  color: #333;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 700;
  line-height: 24px;
}

.resolution-card {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  border-radius: 12px;
  background: #fff5f5;

  .resolution-wrapper.mobile & {
    border-radius: 0;
    margin: 0 -16px;
    gap: 16px;
  }
}

.keyword-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.keyword-tag {
  padding: 3px 6px;
  background: rgba(255, 110, 110, 0.07);
  border-radius: 4px;
  color: #ff6e6e;
  font-family: Pretendard;
  font-size: 13px;
  font-weight: 700;
  line-height: 18px;
}

.feature-list {
  display: flex;
  flex-direction: column;
  gap: 10px;

  .resolution-wrapper.mobile & {
    gap: 16px;
  }
}

.feature-item {
  padding: 12px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 8px;
  color: #333;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 400;
  line-height: 24px;
}

// 하단 문구
.bottom-quote {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding-bottom: 24px;

  .resolution-wrapper.mobile & {
    padding: 0 16px;
  }

  .img_year_0805 {
    display: flex;
    width: 100%;
    max-width: 399px;
    aspect-ratio: 399/134;
    background: url('/src/assets/images_manse/yearly-fortune/img_year_0805.png') center center
      no-repeat;
    background-size: contain;
  }

  .icon-quote {
    width: 28px;
    height: 22px;
    background: url('/src/assets/images_manse/icon_quotes.svg') center center no-repeat;
    background-size: contain;
  }

  .quote-text {
    color: #333;
    font-family: Pretendard;
    font-size: 16px;
    font-weight: 400;
    line-height: 28px;
    text-align: center;
  }
}

.strategy-wrapper.mobile {
  padding: 24px 0;
  gap: 26px;

  .flow-section {
    padding: 0 16px;
  }

  .insight-card {
    flex-direction: column;
    align-items: center;
    border-radius: 0;
    padding: 16px;
    gap: 10px;

    &.yellow {
      background: #fefbf3;
    }

    &.blue {
      background: #f3f7ff;
    }

    .card-icon {
      width: 97px;
      height: 110px;
      display: flex;
      flex-shrink: 0;
    }

    .card-body {
      display: flex;
      flex: 1;
      word-break: break-all;
      width: 100%;
    }
  }
}
</style>

<style lang="scss">
.bottom-quote .quote-text mark {
  background: transparent;
  color: #be3331;
  font-weight: 700;
  padding: 0 6px;
  margin: 0 -6px;
  background-image: linear-gradient(transparent 70%, rgba(190, 51, 49, 0.15) 70%);
}
</style>
